<template>
  <div class="page">
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
      <h2 class="page-title" style="margin:0;">{{ $t('menu.adminAnnouncements') }}</h2>
      <el-button type="primary" class="btn-glass" @click="createDialog=true">{{ $t('admin.create') }}</el-button>
    </div>

    <div class="glass-card" style="margin-top:14px; padding:14px;">
      <el-table :data="items" style="width:100%;">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="title" :label="$t('field.title')" />
        <el-table-column prop="publishedAt" :label="$t('field.createdAt')" width="190">
          <template slot-scope="s">
            {{ s.row.publishedAt ? new Date(s.row.publishedAt).toLocaleString() : '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.actions')" width="160" class-name="actions-col">
          <template slot-scope="s">
            <el-badge :value="s.row.newConfirmed" :max="9" :hidden="(s.row.newConfirmed||0)<=0" class="badge-action">
              <el-button size="mini" @click="openStats(s.row)">{{ $t('admin.viewConfirmations') }}</el-button>
            </el-badge>
          </template>
        </el-table-column>
      </el-table>

      <div style="display:flex;justify-content:flex-end;margin-top:12px;">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page="page"
          :page-sizes="[5,10,20,50]"
          @current-change="onPageChange"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <el-dialog
      :title="$t('admin.create')"
      :visible.sync="createDialog"
      width="560px"
      :modal="false"
      :lock-scroll="false"
      :close-on-click-modal="false"
    >
      <el-form :model="createForm" label-position="top">
        <el-form-item :label="$t('field.title')">
          <el-input v-model="createForm.title" />
        </el-form-item>
        <el-form-item :label="$t('field.message')">
          <el-input v-model="createForm.content" type="textarea" :rows="8" />
        </el-form-item>
        <el-form-item :label="$t('admin.recipients')">
          <div style="display:flex;gap:8px;align-items:center;margin-bottom:8px;flex-wrap:wrap;">
            <el-button size="mini" @click="selectAllUsers">{{ $t('common.selectAll') }}</el-button>
            <el-button size="mini" @click="invertUsers">{{ $t('common.invert') }}</el-button>
            <el-button size="mini" @click="clearUsers">{{ $t('common.clear') }}</el-button>
            <span class="muted" style="font-size:12px;">{{ createForm.userIds.length }}/{{ users.length }}</span>
          </div>
          <el-select v-model="createForm.userIds" multiple filterable style="width: 100%;">
            <el-option v-for="u in users" :key="u.id" :value="u.id" :label="`${u.displayName} (${u.username})`" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="createDialog=false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" class="btn-glass" :loading="saving" @click="create">{{ $t('common.confirm') }}</el-button>
      </span>
    </el-dialog>

    <el-drawer
      :title="$t('admin.viewConfirmations')"
      :visible.sync="statsDrawer"
      size="520px"
      :with-header="true"
      :modal="false"
      :append-to-body="false"
    >
      <div style="padding:16px;">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;">
          <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ statsAnnouncement ? statsAnnouncement.title : '-' }}</div>
          <span class="tag-pill">#{{ statsAnnouncement ? statsAnnouncement.id : '-' }}</span>
        </div>
        <div class="muted" style="margin-top:6px;">
          {{ statsAnnouncement && statsAnnouncement.publishedAt ? new Date(statsAnnouncement.publishedAt).toLocaleString() : '-' }}
        </div>

        <div class="glass-card" style="margin-top:12px;padding:12px;">
          <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;">
            <div style="font-family:'Poppins',sans-serif;font-weight:600;">{{ $t('admin.viewConfirmations') }}</div>
            <div class="muted" style="font-size:12px;">
              {{ confirmedCount }} {{ $t('admin.confirmed') }} / {{ statsItems.length }} {{ $t('admin.recipients') }}
            </div>
          </div>
          <div v-if="statsLoading" class="muted" style="margin-top:8px;">{{ $t('common.loading') }}</div>
          <el-table v-else :data="statsItems" style="width:100%; margin-top:10px;">
            <el-table-column prop="displayName" :label="$t('admin.displayName')" />
            <el-table-column prop="username" :label="$t('admin.username')" width="150" />
            <el-table-column prop="confirmed" :label="$t('admin.confirmed')" width="120">
              <template slot-scope="s">
                <span :class="['tag-pill', s.row.confirmed ? '' : 'tag-pill--warn']">
                  {{ s.row.confirmed ? $t('admin.confirmed') : $t('admin.unconfirmed') }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="confirmedAt" :label="$t('common.confirm')" width="190">
              <template slot-scope="s">
                {{ s.row.confirmedAt ? new Date(s.row.confirmedAt).toLocaleString() : '-' }}
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { adminPageAnnouncements, adminCreateAnnouncement, adminListUsers, adminAnnouncementStats, adminMarkAnnouncementSeen } from '../../api/admin';

export default {
  name: 'AdminAnnouncements',
  data() {
    return {
      items: [],
      total: 0,
      page: 1,
      pageSize: 10,
      users: [],
      saving: false,
      createDialog: false,
      createForm: { title: '', content: '', userIds: [] },
      statsDrawer: false,
      statsLoading: false,
      statsAnnouncement: null,
      statsItems: []
    };
  },
  created() {
    this.load();
  },
  computed: {
    confirmedCount() {
      return (this.statsItems || []).filter((x) => x && x.confirmed).length;
    },
    badgeTotal() {
      return this.$store.state.adminBadges ? this.$store.state.adminBadges.announcements : 0;
    },
    version() {
      return this.$store.state.versions.announcements;
    }
  },
  watch: {
    version() {
      this.load();
    }
  },
  methods: {
    async load() {
      const [paged, users] = await Promise.all([
        adminPageAnnouncements({ page: this.page - 1, size: this.pageSize }),
        adminListUsers()
      ]);
      this.items = paged.items || [];
      this.total = paged.total || 0;
      this.users = (users || []).filter((u) => (u.role === 'USER' || u.role === 'SECONDARY_HANDLER') && !u.deletedAt);
      this.$store.dispatch('refreshAdminBadges');
    },
    onPageChange(p) {
      this.page = p;
      this.load();
    },
    onSizeChange(s) {
      this.pageSize = s;
      this.page = 1;
      this.load();
    },
    async create() {
      if (!this.createForm.title || !this.createForm.content) return this.$message.error(this.$t('common.required'));
      if (!this.createForm.userIds || this.createForm.userIds.length === 0) return this.$message.error(this.$t('common.required'));
      this.saving = true;
      try {
        await adminCreateAnnouncement(this.createForm);
        this.createDialog = false;
        this.createForm = { title: '', content: '', userIds: [] };
        this.page = 1;
        await this.load();
      } finally {
        this.saving = false;
      }
    },
    selectAllUsers() {
      this.createForm.userIds = this.users.map((u) => u.id);
    },
    invertUsers() {
      const chosen = new Set(this.createForm.userIds || []);
      this.createForm.userIds = this.users.filter((u) => !chosen.has(u.id)).map((u) => u.id);
    },
    clearUsers() {
      this.createForm.userIds = [];
    },
    async openStats(row) {
      this.statsAnnouncement = row;
      this.statsDrawer = true;
      this.statsLoading = true;
      try {
        this.statsItems = await adminAnnouncementStats(row.id);
        await adminMarkAnnouncementSeen(row.id);
        await this.$store.dispatch('refreshAdminBadges');
        await this.load();
      } finally {
        this.statsLoading = false;
      }
    }
  }
};
</script>
